import { SelectChangeEvent } from '@mui/material';
import { ChangeEventHandler, FocusEventHandler, useState, AnimationEventHandler } from 'react';
import { FormLabels, useLabelShrink } from './useLabelShrink';
import { FormErrors, useValidation, ValidationResult } from './useValidation';

export type FormState<T extends string> = Record<T, string>;

export type FormFields<T extends string> = Record<T, FormFieldDescriptor<T>>;

export interface FormFieldDescriptor<T extends string> {
  name: T,
  validate?: (value: string, state?: FormState<T>) => ValidationResult,
  initialValue: string,
  isRequired?: boolean,
  requiredMessage?: string,
  dependentFields?: T[],
  validateEmpty?: boolean
}

export interface UseFormResult<T extends string> {
  handleChange: ChangeEventHandler<HTMLInputElement>,
  handleSelect: (e: SelectChangeEvent<string>) => void,
  handleAutofill: AnimationEventHandler<HTMLInputElement>,
  handleBlur: FocusEventHandler<HTMLInputElement>,
  handleFocus: FocusEventHandler<HTMLInputElement>,
  getFormValidState: () => boolean,
  state: FormState<T>,
  errors: FormErrors<T>,
  labels: FormLabels<T>,
  isValid: boolean,
  setFieldValues: (values: Partial<FormState<T>>) => void,
  resetForm?: () => void,
}

export const useForm = <T extends string>(fields: FormFields<T>): UseFormResult<T> => {
  const [ state, setState ] = useState(() => (
    (Object.keys(fields) as T[]).reduce((newFields, fieldKey) => {
      const { name: fieldName, initialValue } = fields[fieldKey];

      return ({
        ...newFields,
        [fieldName]: initialValue
      });
    }, {} as FormState<T>)
  ));

  const {
    errors,
    isValid,
    validateFields,
    validateAllFields,
    resetErrors
  } = useValidation<T>(fields);

  const {
    labels,
    setLabelShrink
  } = useLabelShrink<T>(fields);

  const isFieldNameDefined = (fieldName: string): fieldName is T => (
    ((Object.values(fields) as FormFieldDescriptor<T>[])
      .map(field => field.name)).includes(fieldName as T)
  );

  const handleChange: ChangeEventHandler<HTMLInputElement> = e => {
    const { name: fieldName, value } = e.target;
    if (isFieldNameDefined(fieldName)) {
      const newState = {
        ...state,
        [fieldName]: value
      };
      setState(newState);

      const prevValue = state[fieldName] || '';
      // Detect autofill for non-MUI inputs
      const isSignificantChange = value !== prevValue && (
        Math.abs(value.length - prevValue.length) > 2 ||
        (prevValue.length > 0 && !value.includes(prevValue) && !prevValue.includes(value))
      );

      if ((e.target as HTMLInputElement).classList.contains('AUTOFILLED') || isSignificantChange) {
        validateFields([ fieldName ], newState);
      }
    }
  };

  const handleBlur: FocusEventHandler<HTMLInputElement> = e => {
    const { name: fieldName, value: fieldValue } = e.target;
    if (isFieldNameDefined(fieldName)) {
      if (fieldValue === '') {
        setLabelShrink(fieldName, false);
      }
      validateFields([ fieldName ], state);
    }
  };

  // We are adding/removing AUTOFILLED class so we can validate field on change
  // if input has this class otherwise we're doing it on blur
  const handleAutofill: AnimationEventHandler<HTMLInputElement> = e => {
    const { name: fieldName } = e.target as HTMLInputElement;

    if (e.animationName.includes('onAutoFillStart')) {
      (e.target as HTMLInputElement).classList.add('AUTOFILLED');
      if (isFieldNameDefined(fieldName)) {
        setLabelShrink(fieldName, true);
      }
    } else if (e.animationName.includes('onAutoFillCancel')) {
      if ((e.target as HTMLInputElement).classList.contains('AUTOFILLED')) {
        (e.target as HTMLInputElement).classList.remove('AUTOFILLED');
        if (isFieldNameDefined(fieldName)) {
          setLabelShrink(fieldName, false);
        }
      }
    }
  };

  const handleSelect = (e: SelectChangeEvent<string>): void => {
    const { name: fieldName, value } = e.target;

    if (isFieldNameDefined(fieldName)) {
      const newState = {
        ...state,
        [fieldName]: value
      };

      setState(newState);
      validateFields([ fieldName ], newState);
    }
  };

  const handleFocus: FocusEventHandler<HTMLInputElement> = e => {
    const { name: fieldName } = e.target;

    if (isFieldNameDefined(fieldName)) {
      setLabelShrink(fieldName, true);
    }
  };

  const setFieldValues = (newFieldValues: Partial<FormState<T>>): void => {
    const newState = {
      ...state,
      ...newFieldValues
    };

    const newFieldNames = Object.keys(newFieldValues) as T[];

    setState(newState);
    validateFields(newFieldNames, newState);
  };

  const getFormValidState = (): boolean => (
    validateAllFields(state)
  );

  const initialState = (Object.keys(fields) as T[]).reduce((acc, fieldKey) => {
    const { name: fieldName, initialValue } = fields[fieldKey];

    return { ...acc, [fieldName]: initialValue };
  }, {} as FormState<T>);

  const resetForm = (): void => {
    setState(initialState);
    resetErrors();
  };

  return {
    handleChange,
    handleBlur,
    handleSelect,
    handleAutofill,
    handleFocus,
    getFormValidState,
    state,
    labels,
    errors,
    isValid,
    setFieldValues,
    resetForm
  };
};
